import { Link } from "@tanstack/react-router";
import { ArrowRight, BookOpen, Heart } from "lucide-react";
import { Button } from "@project/components/ui/button";
import {
  formatJainMonth,
  formatPakshaFortnight,
  formatPanchangHeadingDate,
  formatTithiBadge,
} from "@project/lib/jain-panchang-utils";
import type { JainPanchang } from "@project/types/jain-panchang";

type PanchangTodayHighlightProps = {
  entry: JainPanchang;
  onViewDetails: (entry: JainPanchang) => void;
  labels: {
    eyebrow: string;
    jainMonth: string;
    paksha: string;
    currentTithi: string;
    significance: string;
    viewDetails: string;
    donateToday: string;
  };
};

export function PanchangTodayHighlight({
  entry,
  onViewDetails,
  labels,
}: PanchangTodayHighlightProps) {
  const significance =
    entry.significance ??
    entry.short_description ??
    entry.religious_occasion ??
    "";

  return (
    <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
      <div className="grid lg:grid-cols-[1.15fr_0.85fr]">
        <div className="relative border-l-[6px] border-gold p-6 sm:p-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            {labels.eyebrow}
          </p>
          <h2 className="mt-3 font-serif text-2xl text-foreground sm:text-3xl">
            {formatPanchangHeadingDate(entry)}
          </h2>

          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {labels.jainMonth}
              </p>
              <p className="mt-1 text-base font-semibold text-foreground">
                {formatJainMonth(entry.jain_month)} Month
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {labels.paksha}
              </p>
              <p className="mt-1 flex items-center gap-2 text-base font-semibold text-foreground">
                <span className="h-2 w-2 rounded-full bg-gold" />
                {formatPakshaFortnight(entry.paksha)}
              </p>
            </div>
          </div>

          <div className="mt-6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {labels.currentTithi}
            </p>
            <span className="bg-gold/15 mt-2 inline-flex rounded-full px-4 py-1.5 text-sm font-semibold text-gold">
              {formatTithiBadge(entry.jain_tithi)}
            </span>
          </div>
        </div>

        <div className="bg-secondary/35 flex h-full flex-col border-t border-border p-6 sm:p-8 lg:border-l lg:border-t-0">
          <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.16em] text-foreground">
            <BookOpen className="h-4 w-4 text-primary" />
            {labels.significance}
          </div>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            {significance}
          </p>

          <div className="mt-auto flex flex-col gap-4 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={() => onViewDetails(entry)}
              className="hover:text-primary/80 inline-flex items-center gap-1 text-sm font-semibold text-primary transition"
            >
              {labels.viewDetails}
              <ArrowRight className="h-4 w-4" />
            </button>
            <Button
              asChild
              className="hover:bg-gold/90 rounded-full bg-gold text-gold-foreground"
            >
              <Link to="/donate">
                <Heart className="mr-2 h-4 w-4" />
                {labels.donateToday}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
